import React, { createContext, useContext, useEffect, useState } from 'react';
import { onMessage } from 'firebase/messaging';
import { messaging } from '../config/firebase';

const NotificationContext = createContext();

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // Load notifications from local storage on mount
    const savedNotifications = localStorage.getItem('appNotifications');
    if (savedNotifications) {
      try {
        const parsed = JSON.parse(savedNotifications);
        setNotifications(parsed);
        setUnreadCount(parsed.filter((n) => !n.read).length);
      } catch (e) {
        console.error('Failed to parse notifications', e);
      }
    }

    if (messaging) {
      const unsubscribe = onMessage(messaging, (payload) => {
        console.log('Message received. ', payload);
        const { notification, data } = payload;

        const newNotification = {
          id: Date.now(), // Simple ID based on timestamp
          title: notification?.title || data?.title || 'New Notification',
          body: notification?.body || data?.body || '',
          image: notification?.image || data?.image || null,
          url: data?.url || data?.click_action || null,
          type: data?.type || 'alert', // 'alert', 'update', 'book_update'
          timestamp: new Date().toISOString(),
          read: false,
        };

        setNotifications((prev) => {
          const updated = [newNotification, ...prev];
          localStorage.setItem('appNotifications', JSON.stringify(updated));
          return updated;
        });
        setUnreadCount((prev) => prev + 1);

        // Show a native browser notification if the tab is visible but not focused (optional, but good UX)
        // Note: Browsers might block this if not triggered by user interaction, but worth a try or just rely on the UI bell.
        if (Notification.permission === 'granted' && document.hidden) {
          new Notification(newNotification.title, {
            body: newNotification.body,
            icon: newNotification.image || '/logo192.png',
          });
        }
      });

      return () => {
        unsubscribe();
      };
    }
  }, []);

  const markAsRead = (id) => {
    setNotifications((prev) => {
      const updated = prev.map((n) => (n.id === id ? { ...n, read: true } : n));
      localStorage.setItem('appNotifications', JSON.stringify(updated));
      setUnreadCount(updated.filter((n) => !n.read).length);
      return updated;
    });
  };

  const markAllAsRead = () => {
    setNotifications((prev) => {
      const updated = prev.map((n) => ({ ...n, read: true }));
      localStorage.setItem('appNotifications', JSON.stringify(updated));
      setUnreadCount(0);
      return updated;
    });
  };

  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
    localStorage.removeItem('appNotifications');
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        clearNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
